import * as Location from "expo-location";
import type { TFunction } from "i18next";
import { Linking, Platform } from "react-native";

import i18n from "@/i18n";
import { createSessionSingleFlight } from "@/lib/single-flight";
import { locationStrings } from "@/i18n/features/location.i18n";

/**
 * Whether the device (or browser) can actually give opncue a position, and if
 * not, what is standing in the way.
 *
 * Location is not optional here — a venue cannot be identified without it — so
 * every non-ready value routes the user to the onboarding permissions screen,
 * which is the single place opncue asks for anything.
 */
export type LocationAccess =
  | "checking"
  | "ready"
  | "permission-required"
  | "permission-blocked"
  | "services-disabled"
  | "web-insecure"
  | "web-unsupported"
  | "error";

type WebPermissionState = "granted" | "prompt" | "denied";

type WebPermissionStatus = {
  state: WebPermissionState;
  onchange?: (() => void) | null;
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
};

type WebGeolocationError = {
  code: number;
  message?: string;
};

type WebGeolocation = {
  getCurrentPosition: (
    success: () => void,
    error?: (error: WebGeolocationError) => void,
    options?: {
      enableHighAccuracy?: boolean;
      timeout?: number;
      maximumAge?: number;
    },
  ) => void;
};

type WebNavigator = {
  userAgent?: string;
  geolocation?: WebGeolocation;
  permissions?: {
    query: (descriptor: { name: "geolocation" }) => Promise<WebPermissionStatus>;
  };
};

export type WebEnvironment = {
  browserName: string;
  isMac: boolean;
};

const WEB_PERMISSION_DENIED = 1;
const WEB_POSITION_UNAVAILABLE = 2;
const WEB_POSITION_TIMEOUT = 3;

/** Look, without prompting. Safe to call on launch and on every foreground. */
export async function checkLocationAccess(): Promise<LocationAccess> {
  if (Platform.OS === "web") return checkWebLocationAccess();

  try {
    const permission = await Location.getForegroundPermissionsAsync();

    if (!permission.granted) {
      return permission.canAskAgain
        ? "permission-required"
        : "permission-blocked";
    }

    return (await Location.hasServicesEnabledAsync())
      ? "ready"
      : "services-disabled";
  } catch (error) {
    console.error("Unable to check location access:", error);
    return "error";
  }
}

/**
 * Ask. Must be called from a user gesture on web — that press is what lets the
 * browser show its own prompt.
 */
export async function requestLocationAccess(): Promise<LocationAccess> {
  if (Platform.OS === "web") return requestWebLocationAccess();

  try {
    const permission = await Location.requestForegroundPermissionsAsync();

    if (!permission.granted) {
      return permission.canAskAgain
        ? "permission-required"
        : "permission-blocked";
    }

    return (await Location.hasServicesEnabledAsync())
      ? "ready"
      : "services-disabled";
  } catch (error) {
    console.error("Unable to request location permission:", error);
    return "error";
  }
}

/** The app's own permission page in the OS settings. */
export async function openAppSettings(): Promise<boolean> {
  try {
    await Linking.openSettings();
    return true;
  } catch (error) {
    console.error("Unable to open app settings:", error);
    return false;
  }
}

/**
 * The device-wide Location Services switch, which is a different screen from
 * the app's permissions — Android has a dedicated intent for it.
 */
export async function openLocationSettings(): Promise<boolean> {
  try {
    if (Platform.OS === "android") {
      await Linking.sendIntent("android.settings.LOCATION_SOURCE_SETTINGS");
    } else {
      await Linking.openSettings();
    }
    return true;
  } catch (error) {
    console.error("Unable to open location settings:", error);
    return openAppSettings();
  }
}

/**
 * Notice when someone changes the site permission through the browser's own
 * controls, which fires no other event the app can see.
 */
export function watchWebLocationPermission(onChange: () => void): () => void {
  if (Platform.OS !== "web") return () => {};

  const webNavigator = getWebNavigator();
  if (!webNavigator?.permissions) return () => {};

  let permissionStatus: WebPermissionStatus | undefined;
  let disposed = false;

  const handleChange = () => {
    if (!disposed) onChange();
  };

  void webNavigator.permissions
    .query({ name: "geolocation" })
    .then((result) => {
      if (disposed) return;
      permissionStatus = result;

      if (permissionStatus.addEventListener) {
        permissionStatus.addEventListener("change", handleChange);
      } else {
        permissionStatus.onchange = handleChange;
      }
    })
    .catch((error) => {
      console.warn("Unable to watch browser location permission:", error);
    });

  return () => {
    disposed = true;

    if (permissionStatus?.removeEventListener) {
      permissionStatus.removeEventListener("change", handleChange);
    } else if (permissionStatus) {
      permissionStatus.onchange = null;
    }
  };
}

export function getWebEnvironment(): WebEnvironment {
  if (Platform.OS !== "web") {
    return { browserName: "browser", isMac: false };
  }

  const userAgent = getWebNavigator()?.userAgent ?? "";

  let browserName = "browser";

  if (/Edg\//i.test(userAgent)) {
    browserName = "Microsoft Edge";
  } else if (/Chrome\//i.test(userAgent) && !/Edg\//i.test(userAgent)) {
    browserName = "Chrome";
  } else if (/Firefox\//i.test(userAgent)) {
    browserName = "Firefox";
  } else if (/Safari\//i.test(userAgent) && !/Chrome\//i.test(userAgent)) {
    browserName = "Safari";
  }

  return { browserName, isMac: /Macintosh|Mac OS X/i.test(userAgent) };
}

/**
 * The steps that get someone unstuck, where the app itself cannot do it for
 * them. Only the browser cases have any — every native case is one button.
 */
export function getLocationAccessSteps(
  access: LocationAccess,
  { browserName, isMac }: WebEnvironment,
  t: TFunction,
): string[] | undefined {
  if (Platform.OS !== "web") return undefined;

  switch (access) {
    case "permission-blocked":
      return getBrowserBlockedSteps(browserName, t);

    case "services-disabled":
      return isMac
        ? [
            t(locationStrings.macOpenSettings),
            t(locationStrings.macOpenLocation),
            t(locationStrings.enableBrowser, { browser: browserName }),
            t(locationStrings.returnCheck),
          ]
        : [
            t(locationStrings.enableDevice),
            t(locationStrings.allowBrowser, { browser: browserName }),
            t(locationStrings.returnCheck),
          ];

    case "web-insecure":
      return [
        t(locationStrings.openHttps),
        t(locationStrings.useLocalhost),
        t(locationStrings.reloadCheck),
      ];

    case "web-unsupported":
      return [
        t(locationStrings.currentBrowser),
        t(locationStrings.extensions),
        t(locationStrings.reloadRetry),
      ];

    default:
      return undefined;
  }
}

function getBrowserBlockedSteps(browserName: string, t: TFunction): string[] {
  switch (browserName) {
    case "Chrome":
      return [
        t(locationStrings.chromeControls),
        t(locationStrings.allowSite),
        t(locationStrings.returnCheck),
      ];

    case "Microsoft Edge":
      return [
        t(locationStrings.edgeSiteInfo),
        t(locationStrings.edgePermissions),
        t(locationStrings.returnCheck),
      ];

    case "Safari":
      return [
        t(locationStrings.safariSettings),
        t(locationStrings.safariLocation),
        t(locationStrings.returnCheck),
      ];

    case "Firefox":
      return [
        t(locationStrings.firefoxPermissions),
        t(locationStrings.firefoxAllow),
        t(locationStrings.returnCheck),
      ];

    default:
      return [
        t(locationStrings.genericPermissions),
        t(locationStrings.genericAllow),
        t(locationStrings.returnCheck),
      ];
  }
}

async function checkWebLocationAccess(): Promise<LocationAccess> {
  const webNavigator = getWebNavigator();

  if (!webNavigator?.geolocation) return "web-unsupported";
  if (getSecureContextStatus() === false) return "web-insecure";

  // Some browsers do not expose the Permissions API at all. Fall through to the
  // button, which asks for a real position instead.
  if (!webNavigator.permissions) return "permission-required";

  try {
    const permission = await webNavigator.permissions.query({
      name: "geolocation",
    });

    if (permission.state === "denied") return "permission-blocked";
    if (permission.state === "prompt") return "permission-required";

    // Granted. Confirm the browser can actually produce a position — on macOS
    // it often cannot, even with the site permission allowed.
    await requestWebPosition();
    return "ready";
  } catch (error) {
    console.error("Unable to check browser location:", error);
    return "error";
  }
}

async function requestWebLocationAccess(): Promise<LocationAccess> {
  const webNavigator = getWebNavigator();

  if (!webNavigator?.geolocation) return "web-unsupported";
  if (getSecureContextStatus() === false) return "web-insecure";

  try {
    await requestWebPosition();
    return "ready";
  } catch (error) {
    const locationError = normalizeWebLocationError(error);
    console.error("Browser location request failed:", locationError);

    switch (locationError.code) {
      case WEB_PERMISSION_DENIED:
        return classifyWebPermissionDenied();

      case WEB_POSITION_UNAVAILABLE:
        return "services-disabled";

      case WEB_POSITION_TIMEOUT:
      default:
        return "error";
    }
  }
}

/**
 * What a geolocation PERMISSION_DENIED actually means.
 *
 * Code 1 is not only "this site is denied". The browser returns it for at
 * least three different situations, and this used to map all of them to
 * `permission-blocked`:
 *
 *   - the site permission really is Deny;
 *   - the user dismissed the prompt without answering;
 *   - the BROWSER cannot get a position at all, most often because macOS
 *     Location Services is switched off for it. No prompt is ever shown and
 *     the call fails immediately.
 *
 * In the last two the site permission is still "Ask", so the user was sent to
 * change a setting that was already correct — reported on Safari, 2026-09-05,
 * with the site plainly set to Ask and the card insisting the browser was
 * preventing the request. Following those steps changes nothing, which is the
 * worst kind of instruction: one that cannot be complied with.
 *
 * The Permissions API separates them, and `checkWebLocationAccess` already
 * consults it — this is the same question asked on the path that had skipped
 * it. Each answer is reported as exactly what it establishes, and no further:
 *
 *   denied   the site is the obstacle. The browser's own steps apply.
 *   granted  the site allows it and the position was STILL refused, so the
 *            obstacle is below the site — the browser's access to the system.
 *            Unambiguous, and what `services-disabled` means; on a Mac its
 *            steps already point at Location Services.
 *   prompt   the site may still be asked. Asking again is the only thing that
 *            can change the answer, so the card returns to its ask.
 *
 * "prompt" was briefly reported as `services-disabled` too, on the reasoning
 * that macOS Location Services being off for the browser is the usual cause of
 * a code 1 that shows no prompt. That was disproven the same day by the device
 * it was written for — Location Services was enabled for Safari — and it is
 * recorded here because the mistake is the interesting part: it inferred a
 * SPECIFIC cause from evidence that only ruled one out. A dismissed prompt
 * leaves exactly the same reading and is fixed by asking again, not by a trip
 * to System Settings.
 *
 * What is still not distinguishable from inside the browser: a dismissed
 * prompt versus a browser the system refuses while the site is on "Ask". Both
 * read "prompt". Rather than guess between them the reason is LOGGED, which is
 * how the first guess here was caught.
 *
 * Without the Permissions API (Safari before 16) nothing can be distinguished,
 * so the previous answer stands.
 */
async function classifyWebPermissionDenied(): Promise<LocationAccess> {
  const webNavigator = getWebNavigator();
  if (!webNavigator?.permissions) return "permission-blocked";

  try {
    const permission = await webNavigator.permissions.query({
      name: "geolocation",
    });
    console.error(
      `Browser refused a position while the site permission is "${permission.state}"`,
    );
    switch (permission.state) {
      case "denied":
        return "permission-blocked";
      case "granted":
        return "services-disabled";
      default:
        return "permission-required";
    }
  } catch (error) {
    console.error("Unable to read the site location permission:", error);
    return "permission-blocked";
  }
}

/**
 * One position request at a time, shared by everyone who asks while it runs.
 *
 * Answering the browser's permission prompt IS a permission change, so the
 * moment the user clicks Allow, `use-location-access-watch` fires `refresh()`
 * — and `checkWebLocationAccess`, now seeing "granted", starts a SECOND
 * `getCurrentPosition` while the one from the button press is still settling.
 * The user's press caused both, but only the first is tied to their gesture.
 *
 * Two concurrent high-accuracy requests against one CoreLocation session is not
 * something to hand a browser and hope: whichever one loses reports a failure
 * for a permission that was just granted, and `setAccess` bumps the store's
 * request counter, so the loser's answer is the one that sticks. Reported as
 * "I clicked Allow and got the Open settings screen" — Safari, 2026-09-05.
 *
 * Coalescing removes the race rather than ordering it. Both callers want the
 * same answer at the same instant, so there is nothing to lose by giving them
 * the same promise, and the second request stops existing.
 */
// No session scoping here: a browser's location permission belongs to the
// browser, not to whoever is signed in, so every caller shares one slot. The
// epoch parameter is what makes this helper safe for session-scoped reads; it
// is a constant for this one.
const WEB_POSITION_EPOCH = 0;
const webPositionFlight = createSessionSingleFlight<void>();

function requestWebPosition(): Promise<void> {
  return webPositionFlight(WEB_POSITION_EPOCH, () => {
    return new Promise<void>((resolve, reject) => {
      const webNavigator = getWebNavigator();

      if (!webNavigator?.geolocation) {
        reject({
          code: 0,
          message: i18n.t("location.editor.geolocationUnavailable"),
        });
        return;
      }

      webNavigator.geolocation.getCurrentPosition(
        () => resolve(),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
      );
    });
  });
}

function normalizeWebLocationError(error: unknown): WebGeolocationError {
  if (typeof error === "object" && error !== null && "code" in error) {
    const possibleError = error as { code?: unknown; message?: unknown };

    return {
      code: typeof possibleError.code === "number" ? possibleError.code : 0,
      message:
        typeof possibleError.message === "string"
          ? possibleError.message
          : undefined,
    };
  }

  return {
    code: 0,
    message: error instanceof Error ? error.message : "Unknown location error.",
  };
}

function getWebNavigator() {
  const globalWithNavigator = globalThis as unknown as {
    navigator?: WebNavigator;
  };

  return globalWithNavigator.navigator ?? null;
}

function getSecureContextStatus() {
  const webGlobal = globalThis as unknown as { isSecureContext?: boolean };
  return webGlobal.isSecureContext;
}
